import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { soundQueryKey, useLookupVoice, validateCatalogVoice } from "./api";
import { buildVoiceCatalogDraftValues } from "./catalogDraft";
import {
  formatSoundCatalogMutationError,
  submitSoundCatalogChange,
} from "./catalogWorkflow";
import type {
  CatalogVoice,
  SoundVoicesPayload,
  ValidateVoiceResponse,
  VoiceNormalizedProfile,
} from "./contracts";
import { VoicePreviewButton } from "./VoicePreviewButton";

type WorkflowState =
  | "lookup"
  | "curate"
  | "saving"
  | "readback_pending"
  | "approval_required";

function profileFromValidation(validation: ValidateVoiceResponse) {
  const labels = validation.voice.labels;
  return {
    gender: labels.gender ?? "",
    age: labels.age ?? "",
    accent: labels.accent ?? "",
    language: labels.language ?? "",
    locale: labels.locale ?? "",
    use_case: labels.use_case ?? "narration",
  };
}

export function AddVoiceDialog({
  payload,
  initialVoiceId = "",
  triggerLabel = "Add voice",
  triggerVariant = "default",
}: {
  payload: SoundVoicesPayload;
  initialVoiceId?: string;
  triggerLabel?: string;
  triggerVariant?: "default" | "outline" | "secondary";
}) {
  const renderProfileIds = useMemo(
    () => payload.render_profiles.map((profile) => profile.profile_id).sort(),
    [payload.render_profiles],
  );
  const defaultRenderProfileId = renderProfileIds[0] ?? "";
  const [open, setOpen] = useState(false);
  const [voiceId, setVoiceId] = useState(initialVoiceId);
  const [validation, setValidation] = useState<ValidateVoiceResponse | null>(
    null,
  );
  const [profile, setProfile] = useState<VoiceNormalizedProfile>({});
  const [qualityStatus, setQualityStatus] = useState("approved");
  const [renderProfileId, setRenderProfileId] = useState(
    defaultRenderProfileId,
  );
  const [previewReviewed, setPreviewReviewed] = useState(false);
  const [reason, setReason] = useState("");
  const [ticketReference, setTicketReference] = useState("");
  const [state, setState] = useState<WorkflowState>("lookup");
  const [message, setMessage] = useState("");
  const lookupMutation = useLookupVoice();
  const queryClient = useQueryClient();

  const trimmedId = voiceId.trim();
  const voiceIdLooksValid = /^[A-Za-z0-9_-]{8,64}$/.test(trimmedId);
  const canSubmit = useMemo(() => {
    const requiredTraits: Array<keyof VoiceNormalizedProfile> = [
      "gender",
      "age",
      "accent",
      "language",
      "locale",
      "use_case",
    ];
    return Boolean(
      validation &&
        previewReviewed &&
        requiredTraits.every((trait) => profile[trait]?.trim()) &&
        reason.trim().length >= 8 &&
        ticketReference.trim().length >= 3 &&
        renderProfileId.trim() &&
        qualityStatus === "approved",
    );
  }, [
    previewReviewed,
    profile,
    qualityStatus,
    reason,
    renderProfileId,
    ticketReference,
    validation,
  ]);

  const reset = () => {
    setVoiceId(initialVoiceId);
    setValidation(null);
    setProfile({});
    setQualityStatus("approved");
    setRenderProfileId(defaultRenderProfileId);
    setPreviewReviewed(false);
    setReason("");
    setTicketReference("");
    setState("lookup");
    setMessage("");
  };

  const lookup = async () => {
    setMessage("");
    if (!voiceIdLooksValid) {
      setMessage("Enter the exact ElevenLabs voice ID.");
      return;
    }
    try {
      const result = await lookupMutation.mutateAsync(trimmedId);
      if (result.catalog_status !== "uncurated") {
        setMessage(`This voice is already ${result.catalog_status}.`);
        return;
      }
      if (!result.voice.available) {
        setMessage("This provider voice is not currently available.");
        return;
      }
      setValidation(result);
      setProfile(profileFromValidation(result));
      setState("curate");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Voice validation failed.",
      );
    }
  };

  const createDraft = async () => {
    if (!validation || !canSubmit) return;
    setState("saving");
    setMessage("");
    try {
      const currentProvider = await validateCatalogVoice({
        voice_id: validation.voice.voice_id,
        normalized_profile: cleanProfile(profile),
        quality_status: qualityStatus,
        render_profile_id: renderProfileId,
        product_flows: ["b2c"],
        reason: reason.trim(),
        ticket_reference: ticketReference.trim(),
      });
      if (
        currentProvider.voice.provider_metadata_digest !==
        validation.voice.provider_metadata_digest
      ) {
        setValidation(currentProvider);
        setProfile(profileFromValidation(currentProvider));
        setState("curate");
        setPreviewReviewed(false);
        setMessage(
          "Provider metadata changed after validation. Review the refreshed voice before continuing.",
        );
        return;
      }
      if (!currentProvider.voice.available) {
        setState("curate");
        setMessage(
          "The provider/catalog state changed. Refresh Sound and validate again.",
        );
        return;
      }

      const catalogVoice: CatalogVoice = {
        ...(currentProvider.candidate_catalog_entry ?? {
          voice_id: validation.voice.voice_id,
          display_name: validation.voice.name,
          enabled: true,
          product_flows: ["b2c"],
          normalized_profile: cleanProfile(profile),
          reference_names: [],
          quality_status: qualityStatus,
          render_profile_id: renderProfileId,
          provider_category_at_approval: validation.voice.category,
          provider_metadata_digest: validation.voice.provider_metadata_digest,
        }),
        product_flows: ["b2c"],
      };
      const result = await submitSoundCatalogChange({
        buildValues: (activeValues) =>
          buildVoiceCatalogDraftValues(activeValues, catalogVoice),
        reason: reason.trim(),
        ticketReference: ticketReference.trim(),
      });

      if (result.status === "approval_required") {
        setState("approval_required");
        setMessage(
          `Change request ${result.draft.id} was created. An authorized administrator can approve and publish it from the Sound page.`,
        );
      } else {
        setState("readback_pending");
        setMessage(
          "Published to staging. The voice will appear as Live once the system confirms the update, usually within a minute.",
        );
      }
      await queryClient.invalidateQueries({ queryKey: soundQueryKey });
    } catch (error) {
      setState("curate");
      setMessage(formatSoundCatalogMutationError(error));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={triggerVariant}
          size={initialVoiceId ? "sm" : "default"}
        >
          <Plus className="mr-2 size-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a voice from ElevenLabs</DialogTitle>
          <DialogDescription>
            Paste the voice ID from the ElevenLabs library to make that voice
            available for Echoes. Nothing in ElevenLabs is changed or deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sound-voice-id">Voice ID</Label>
            <div className="flex gap-2">
              <Input
                id="sound-voice-id"
                value={voiceId}
                disabled={state !== "lookup"}
                onChange={(event) => setVoiceId(event.target.value)}
                placeholder="ElevenLabs voice ID"
              />
              <Button
                type="button"
                variant="outline"
                disabled={
                  !voiceIdLooksValid ||
                  lookupMutation.isPending ||
                  state !== "lookup"
                }
                onClick={lookup}
              >
                {lookupMutation.isPending ? "Checking…" : "Look up"}
              </Button>
            </div>
          </div>

          {validation ? (
            <>
              <div className="rounded-md border p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{validation.voice.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {validation.voice.voice_id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{validation.voice.category}</Badge>
                    <VoicePreviewButton
                      voiceId={validation.voice.voice_id}
                      voiceName={validation.voice.name}
                      previewUrl={validation.voice.preview_url}
                    />
                  </div>
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={previewReviewed}
                    onCheckedChange={(checked) =>
                      setPreviewReviewed(checked === true)
                    }
                  />
                  I listened to the preview and this is the right voice.
                </label>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {(
                  [
                    "gender",
                    "age",
                    "accent",
                    "language",
                    "locale",
                    "use_case",
                  ] as const
                ).map((field) => (
                  <div key={field} className="space-y-1">
                    <Label htmlFor={`sound-${field}`}>
                      {field.replace("_", " ")}
                    </Label>
                    <Input
                      id={`sound-${field}`}
                      value={profile[field] ?? ""}
                      onChange={(event) =>
                        setProfile((current) => ({
                          ...current,
                          [field]: event.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="sound-quality-status">Quality status</Label>
                  <select
                    id="sound-quality-status"
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    value={qualityStatus}
                    onChange={(event) => setQualityStatus(event.target.value)}
                  >
                    <option value="approved">approved</option>
                    <option value="review">review</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sound-render-profile">Render profile</Label>
                  <select
                    id="sound-render-profile"
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    value={renderProfileId}
                    onChange={(event) => setRenderProfileId(event.target.value)}
                  >
                    {renderProfileIds.map((profileId) => (
                      <option key={profileId} value={profileId}>
                        {profileId}
                      </option>
                    ))}
                  </select>
                  {renderProfileIds.length === 0 ? (
                    <p className="text-xs text-destructive">
                      No applied signed render profile is available.
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="rounded-md bg-muted p-3 text-xs">
                This adds <strong>{validation.voice.name}</strong> to the
                customer voice catalog (B2C only) using the{" "}
                <strong>{renderProfileId}</strong> render settings.
              </div>
              <div className="space-y-1">
                <Label htmlFor="sound-reason">Reason</Label>
                <Input
                  id="sound-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why this voice should be added"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sound-ticket">Ticket/reference</Label>
                <Input
                  id="sound-ticket"
                  value={ticketReference}
                  onChange={(event) => setTicketReference(event.target.value)}
                  placeholder="SND-…"
                />
              </div>
            </>
          ) : null}

          {message ? (
            <p
              role="status"
              className={
                state === "approval_required" || state === "readback_pending"
                  ? "text-sm"
                  : "text-sm text-destructive"
              }
            >
              {message}
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Adding to {payload.environment === "stg" ? "Production" : "Staging"}
            . The voice is used for new Echoes only once it shows as Live in the
            list.
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
          {validation ? (
            <Button
              type="button"
              disabled={!canSubmit || state === "saving"}
              onClick={createDraft}
            >
              {state === "saving"
                ? "Saving…"
                : payload.environment === "stg"
                  ? "Submit for approval"
                  : "Publish to staging"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function cleanProfile(profile: VoiceNormalizedProfile) {
  return Object.fromEntries(
    Object.entries(profile)
      .map(([key, value]) => [key, value?.trim()])
      .filter(([, value]) => value),
  ) as VoiceNormalizedProfile;
}
